import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CANDIDATE_STATUS, getCandidateStatusLabel, EVALUATION_ROUNDS, getEvaluationRoundLabel, CandidateStatus, EvaluationRound } from '@/types/enums';
import { getCandidateStatusHint } from '@/utils/candidateStatus';

interface BulkUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (updateType: 'statusOnly' | 'statusAndRound', newStatus: string, newRound?: string) => void;
  candidateCount: number;
}

export const BulkUpdateDialog: React.FC<BulkUpdateDialogProps> = ({ open, onClose, onConfirm, candidateCount }) => {
  const [updateType, setUpdateType] = useState<'statusOnly' | 'statusAndRound'>('statusOnly');
  const [newStatus, setNewStatus] = useState<string>('');
  const [newRound, setNewRound] = useState<string>('');

  useEffect(() => {
    // Reset fields when dialog opens or updateType changes
    if (open) {
      setNewStatus('');
      setNewRound('');
    }
  }, [open]);

  useEffect(() => {
    // Reset newRound if updateType changes to statusOnly
    if (updateType === 'statusOnly') {
      setNewRound('');
    }
  }, [updateType]);

  const handleConfirm = () => {
    if (!newStatus) return; // Basic validation
    if (updateType === 'statusAndRound' && !newRound) return; // Basic validation

    onConfirm(updateType, newStatus, newRound);
    onClose(); // Close dialog after confirm
  };

  const isConfirmDisabled = !newStatus || (updateType === 'statusAndRound' && !newRound);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Update Candidates</DialogTitle>
          <DialogDescription>
            Update status and/or evaluation round for {candidateCount} selected candidate(s).
            Make sure you have applied all desired filters before proceeding.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="updateType" className="text-right col-span-1">
              Update Type
            </Label>
            <RadioGroup
              defaultValue="statusOnly"
              onValueChange={(value: 'statusOnly' | 'statusAndRound') => setUpdateType(value)}
              className="col-span-3 flex items-center space-x-2"
              id="updateType"
            >
              <div className="flex items-center space-x-1">
                <RadioGroupItem value="statusOnly" id="r1" />
                <Label htmlFor="r1">Status Only</Label>
              </div>
              <div className="flex items-center space-x-1">
                <RadioGroupItem value="statusAndRound" id="r2" />
                <Label htmlFor="r2">Status & Round</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="newStatus" className="text-right col-span-1">
              New Status
            </Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger className="col-span-3" id="newStatus">
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(CANDIDATE_STATUS).map((status) => (
                  <SelectItem key={status} value={status}>
                    {getCandidateStatusLabel(status as CandidateStatus)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="col-span-4 text-xs text-muted-foreground">
            {updateType === 'statusOnly'
              ? 'Status only updates work best when the selected candidates are already in the same stage or round.'
              : getCandidateStatusHint(newRound)}
          </p>

          {updateType === 'statusAndRound' && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="newRound" className="text-right col-span-1">
                New Round
              </Label>
              <Select value={newRound} onValueChange={setNewRound}>
                <SelectTrigger className="col-span-3" id="newRound">
                  <SelectValue placeholder="Select new round" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(EVALUATION_ROUNDS).map((round) => (
                    <SelectItem key={round} value={round}>
                      {getEvaluationRoundLabel(round as EvaluationRound)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isConfirmDisabled}>
            Confirm Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
